import EmptyState from '@/components/common/EmptyState';
import Pagination from '@/components/common/Pagination';
import SelectInput from '@/components/Forms/SelectInput';
import TextInput from '@/components/Forms/TextInput';
import GlassCard from '@/components/GlassCard';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import useRoutes from '@/hooks/useRoutes';
import AppLayout from '@/layouts/AppLayout';
import { Clock, Play, User, Video } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LEVEL_ORDER, LevelKey, LEVELS } from './liveSessionsConfig';
import LiveSessionCard, {
    extractDateFromTitle,
    LanguageLiveSession,
} from './Partials/LiveSessionCard';

interface Props {
    language_live_sessions: LanguageLiveSession[];
}

const ALL_LEVELS = 'all';
const ALL_MONTHS = 'all';
const PAGE_SIZE = 24;

const LanguageLiveSessionsContent: React.FC<Props> = ({
    language_live_sessions,
}) => {
    const { t } = useTranslation();
    const { route } = useRoutes();
    const [levelFilter, setLevelFilter] = useState<
        LevelKey | typeof ALL_LEVELS
    >(ALL_LEVELS);
    const [monthFilter, setMonthFilter] = useState<string>(ALL_MONTHS);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);

    const availableMonths = useMemo(() => {
        const seen = new Map<string, string>();
        language_live_sessions.forEach((session) => {
            const date = extractDateFromTitle(session.title);
            if (!date) return;
            const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            if (!seen.has(key)) {
                seen.set(
                    key,
                    date.toLocaleDateString('es-ES', {
                        month: 'long',
                        year: 'numeric',
                    })
                );
            }
        });
        return Array.from(seen.entries())
            .sort(([a], [b]) => b.localeCompare(a))
            .map(([key, label]) => ({ key, label }));
    }, [language_live_sessions]);

    const filteredSessions = useMemo(() => {
        const term = search.trim().toLowerCase();
        return language_live_sessions.filter((session) => {
            const matchesLevel =
                levelFilter === ALL_LEVELS || session.level === levelFilter;
            const matchesSearch =
                term === '' ||
                session.title.toLowerCase().includes(term) ||
                (session.professor?.name.toLowerCase().includes(term) ?? false);
            let matchesMonth = true;
            if (monthFilter !== ALL_MONTHS) {
                const [year, month] = monthFilter.split('-').map(Number);
                const date = extractDateFromTitle(session.title);
                matchesMonth =
                    date !== null &&
                    date.getFullYear() === year &&
                    date.getMonth() === month - 1;
            }
            return matchesLevel && matchesSearch && matchesMonth;
        });
    }, [language_live_sessions, levelFilter, monthFilter, search]);

    // Reset to the first page whenever the filters narrow or widen the result set.
    useEffect(() => {
        setPage(1);
    }, [levelFilter, monthFilter, search]);

    const totalPages = Math.max(
        1,
        Math.ceil(filteredSessions.length / PAGE_SIZE)
    );
    const pagedSessions = useMemo(
        () => filteredSessions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [filteredSessions, page]
    );

    const hasSessions = language_live_sessions.length > 0;

    return (
        <AppLayout>
            <PageTitle title="Live Sessions Grabadas" />
            <div className="mx-auto max-w-7xl space-y-8">
                {/* Header */}
                <GlassCard className="p-6 shadow-lg">
                    <h1 className="mb-4 text-center text-3xl font-bold text-gray-900 dark:text-white">
                        🎥 {t('languageLiveSessions.title')}
                    </h1>
                    <p className="text-center text-gray-700 dark:text-neutral-300">
                        {t('languageLiveSessions.intro')}
                    </p>
                </GlassCard>

                {/* Levels legend */}
                <GlassCard className="p-6 shadow-lg">
                    <h2 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
                        🎓 {t('liveSessions.legendTitle')}
                    </h2>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {LEVEL_ORDER.map((key) => {
                            const level = LEVELS[key];
                            const cefr = level.cefr.includes('.')
                                ? t(level.cefr)
                                : level.cefr;
                            return (
                                <GlassCard
                                    key={key}
                                    className="flex flex-col overflow-hidden shadow-sm"
                                >
                                    <div
                                        className="h-1 w-full"
                                        style={{ backgroundColor: level.color }}
                                    />
                                    <div className="flex flex-col gap-1.5 p-3">
                                        <span
                                            className="self-start rounded-full px-2.5 py-0.5 text-xs font-semibold text-white"
                                            style={{
                                                backgroundColor: level.color,
                                            }}
                                        >
                                            {t(level.labelKey)}
                                        </span>
                                        <div className="text-xs font-medium text-gray-500 dark:text-neutral-400">
                                            {cefr}
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-neutral-400">
                                            {t(level.descKey)}
                                        </p>
                                    </div>
                                </GlassCard>
                            );
                        })}
                    </div>
                </GlassCard>

                {/* Filters */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <SelectInput
                        label={`🎓 ${t('liveSessions.filterLevel')}`}
                        name="level_filter"
                        value={levelFilter}
                        onChange={(v) =>
                            setLevelFilter(v as LevelKey | typeof ALL_LEVELS)
                        }
                    >
                        <option value={ALL_LEVELS}>
                            {t('liveSessions.allLevels')}
                        </option>
                        {LEVEL_ORDER.map((key) => (
                            <option key={key} value={key}>
                                {t(LEVELS[key].labelKey)}
                            </option>
                        ))}
                    </SelectInput>
                    <SelectInput
                        label="📅 Mes"
                        name="month_filter"
                        value={monthFilter}
                        onChange={(v) => setMonthFilter(v)}
                    >
                        <option value={ALL_MONTHS}>Todos los meses</option>
                        {availableMonths.map(({ key, label }) => (
                            <option key={key} value={key}>
                                {label}
                            </option>
                        ))}
                    </SelectInput>
                    <TextInput
                        label={`🔎 ${t('languageLiveSessions.searchLabel')}`}
                        name="search"
                        value={search}
                        onChange={(v) => setSearch(v)}
                        placeholder={t(
                            'languageLiveSessions.searchPlaceholder'
                        )}
                    />
                </div>

                {/* Recordings grid */}
                {hasSessions ? (
                    filteredSessions.length > 0 ? (
                        <div className="space-y-6">
                            <div className="text-sm text-gray-600 dark:text-neutral-400">
                                {t('languageLiveSessions.showing', {
                                    shown: pagedSessions.length,
                                    total: filteredSessions.length,
                                })}
                            </div>
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                                {pagedSessions.map((session) => {
                                    const level =
                                        LEVELS[session.level] ?? LEVELS.general;
                                    return (
                                        <div
                                            key={session.id}
                                            className="relative flex flex-col sm:after:absolute sm:after:top-0 sm:after:-right-3 sm:after:h-full sm:after:w-px sm:after:bg-black/10 sm:after:content-[''] sm:dark:after:bg-white/10 sm:[&:nth-child(2n)]:after:hidden lg:[&:nth-child(2n)]:after:block lg:[&:nth-child(3n)]:after:hidden"
                                        >
                                            <LiveSessionCard
                                                level={session.level}
                                                title={session.title}
                                                date={extractDateFromTitle(
                                                    session.title
                                                )}
                                                href={route(
                                                    'language_live_session_detail',
                                                    { id: session.id }
                                                ).toString()}
                                                className="flex-1"
                                            >
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="flex flex-col gap-1">
                                                        {session.professor
                                                            ?.name ? (
                                                            <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-neutral-300">
                                                                <User className="h-3.5 w-3.5 shrink-0" />
                                                                <span className="line-clamp-1">
                                                                    {
                                                                        session
                                                                            .professor
                                                                            .name
                                                                    }
                                                                </span>
                                                            </div>
                                                        ) : null}
                                                        {session.duration_minutes >
                                                        0 ? (
                                                            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-neutral-400">
                                                                <Clock className="h-3.5 w-3.5 shrink-0" />
                                                                {t(
                                                                    'languageLiveSessions.duration',
                                                                    {
                                                                        minutes:
                                                                            session.duration_minutes,
                                                                    }
                                                                )}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                    <div
                                                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white transition-transform group-hover:scale-110"
                                                        style={{
                                                            backgroundColor:
                                                                level.color,
                                                        }}
                                                    >
                                                        <Play className="h-4 w-4" />
                                                    </div>
                                                </div>
                                            </LiveSessionCard>
                                        </div>
                                    );
                                })}
                            </div>
                            <Pagination
                                variant="simple"
                                currentPage={page}
                                totalPages={totalPages}
                                hasPrevious={page > 1}
                                hasNext={page < totalPages}
                                onPrevious={() =>
                                    setPage((p) => Math.max(1, p - 1))
                                }
                                onNext={() =>
                                    setPage((p) => Math.min(totalPages, p + 1))
                                }
                            />
                        </div>
                    ) : (
                        <EmptyState
                            description={t('languageLiveSessions.noResults')}
                        />
                    )
                ) : (
                    <EmptyState
                        icon={Video}
                        description={t('languageLiveSessions.empty')}
                    />
                )}
            </div>
        </AppLayout>
    );
};

const LanguageLiveSessions: React.FC<Props> = ({ language_live_sessions }) => (
    <ThemeWrapper>
        <LanguageLiveSessionsContent
            language_live_sessions={language_live_sessions}
        />
    </ThemeWrapper>
);

export default LanguageLiveSessions;
